import { dayLabel } from './dates';
import type { ChainStepRow, InstrumentRow, PartTier, RequirementRow } from '@api/replenishment-engine';
import { formatCount } from '@utils/currency';

/** "2,511 for Blinkit, Instamart (priority 1) · 3,767 for the rest": who a part's units serve, when the day was allotted by channel. */
export const tiersLabel = (tiers: PartTier[] | null | undefined): string | null => {
    if (!tiers?.length) return null;
    return tiers
        .map((t) => `${formatCount(t.qty, { maxDecimals: 0 })} for ${t.priority == null ? 'the rest' : `${t.channelKeys.length ? t.channelKeys.join(', ') : 'ranked channels'} (priority ${t.priority})`}`)
        .join(' · ');
};

/**
 * One dip of one SKU, assembled from its part rows. The view returns one row per part;
 * the dip is productId + seq, its parts ordered by part number. `head` is part 1 (or the
 * part-0 row of a dip with nothing to do) and carries the curve and the dip-level fields.
 */
export interface Dip {
    key: string;
    productId: string;
    seq: number;
    head: RequirementRow;
    /** Every part, ordered by part number. Empty for a part-0 dip (covered or unsourceable). */
    parts: RequirementRow[];
    /** The union of every part's instruments, once each. */
    instruments: InstrumentRow[];
    firstLand: string | null;
    lastLand: string | null;
}

/** The note the backend writes on a transfer that carries a purchase order on from the hub it landed at. */
export const RELAY_NOTE = 'relays a purchase order from the hub';

export const dipKey = (r: Pick<RequirementRow, 'productId' | 'seq'>): string => `${r.productId}|${r.seq}`;

/** The part rows of a view grouped into dips, in the order the first part of each appeared. */
export const groupDips = (rows: RequirementRow[]): Dip[] => {
    const byKey = new Map<string, RequirementRow[]>();
    for (const r of rows) {
        const key = dipKey(r);
        byKey.set(key, [...(byKey.get(key) ?? []), r]);
    }
    return [...byKey.entries()].map(([key, group]) => {
        const sorted = [...group].sort((a, b) => a.part - b.part);
        const parts = sorted.filter((r) => r.part > 0);
        const head = sorted[0];
        const seen = new Set<string>();
        const instruments = sorted.flatMap((r) => r.instruments).filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)));
        const landings = parts.map((r) => r.landDate).filter((d): d is string => Boolean(d)).sort();
        return { key, productId: head.productId, seq: head.seq, head, parts, instruments, firstLand: landings[0] ?? head.landDate, lastLand: landings[landings.length - 1] ?? head.landDate };
    });
};

/** The part rows carrying a given instrument, keyed by instrument id: the run (or FG order) each part is. */
export const partsByInstrument = (rows: RequirementRow[]): Map<string, RequirementRow> => new Map(rows.filter((r) => r.instrumentId).map((r) => [r.instrumentId as string, r]));

/** The name of a material as the part's chain knows it, else the id. */
const materialName = (id: string, chain?: ChainStepRow[]): string => chain?.find((c) => c.itemId === id)?.itemName ?? id;

/** What let a part start, as words: "Amla Extract landing 12 Dec", "material on the floor", "a day of factory capacity". */
export const unlockLabel = (unlockedBy: string | null, chain?: ChainStepRow[]): string => {
    if (unlockedBy == null) return 'material on the floor';
    if (unlockedBy === '__capacity__') return 'a day of factory capacity';
    const step = chain?.find((c) => c.itemId === unlockedBy && c.instrumentId);
    return `${materialName(unlockedBy, chain)} landing${step?.lands ? ` ${dayLabel(step.lands)}` : ''}`;
};

/** What the next part waits on, as words; null on the last part. */
export const waitsOnLabel = (gatingMaterialId: string | null, chain?: ChainStepRow[]): string | null => (gatingMaterialId == null ? null : gatingMaterialId === '__capacity__' ? 'factory capacity' : materialName(gatingMaterialId, chain));

/**
 * The chains of several parts as one list for a drill-down. Steps that carry an instrument
 * (a transfer, an order) are listed once however many parts draw on them; material steps
 * stay per part, since each part needs its own quantity.
 */
export const mergeChains = (parts: RequirementRow[]): Array<ChainStepRow & { part: number }> => {
    const seen = new Set<string>();
    const out: Array<ChainStepRow & { part: number }> = [];
    for (const p of parts) {
        for (const c of p.chain) {
            if (c.instrumentId && c.step !== 'MATERIAL') {
                if (seen.has(c.instrumentId)) continue;
                seen.add(c.instrumentId);
            }
            out.push({ ...c, part: p.part });
        }
    }
    return out;
};

/**
 * The engine's sizing, for a preview: the least quantity landing on `landDay` that keeps the
 * curve at or above zero on every day of the next R days and reaches R days of demand at the
 * end, net of the stock then. A landing later inside the span is netted only from the days
 * after it arrives. Mirrors `sizeFromLanding` in the backend engine.
 */
export const sizeFromLanding = (p: { stock: number[]; demand: number[]; inbound: number[]; onHand: number; landDay: number; refillDays: number }): number => {
    const n = p.demand.length;
    if (p.landDay < 0 || p.landDay >= n) return 0;
    const atStart = Math.max(0, (p.landDay === 0 ? p.onHand : p.stock[p.landDay - 1]) + (p.inbound[p.landDay] ?? 0));
    let need = 0;
    let later = 0;
    let worst = 0;
    for (let d = p.landDay; d < Math.min(n, p.landDay + p.refillDays); d += 1) {
        need += p.demand[d];
        if (d > p.landDay) later += p.inbound[d] ?? 0;
        worst = Math.max(worst, need - later);
    }
    return Math.max(0, Math.ceil(worst - atStart));
};
